'use client';

/**
 * Spaces Usage Tracking Hook
 *
 * Tracks the number of AI room designs a user has generated.
 * On mount it fetches the authoritative count from the server
 * (stored in Supabase app_metadata), with localStorage as a
 * fast-loading fallback until the server responds.
 */

import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import { SPACES_MAX_USAGE } from '@/components/spaces/types';
import type { SpacesUsage } from '@/components/spaces/types';
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY_PREFIX = 'royale_spaces_usage_';

function getStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

function readUsageFromStorage(userId: string): number {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    return typeof parsed.count === 'number' ? parsed.count : 0;
  } catch {
    return 0;
  }
}

function writeUsageToStorage(userId: string, count: number): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(
      getStorageKey(userId),
      JSON.stringify({ count, updatedAt: new Date().toISOString() })
    );
  } catch {
    // Storage full or unavailable - non-critical
  }
}

export function useSpacesUsage(): SpacesUsage & {
  incrementUsage: () => void;
  syncFromServer: (serverCount: number) => void;
} {
  const { customer } = useCustomerAuth();
  const userId = customer?.userId ?? '';

  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!userId) return;

    setCount(readUsageFromStorage(userId));

    let cancelled = false;
    fetch('/api/spaces/generate')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.success) return;
        const serverCount = data.count as number;
        setCount(serverCount);
        writeUsageToStorage(userId, serverCount);
      })
      .catch(() => {
        // Server unreachable; localStorage value is used
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const incrementUsage = useCallback(() => {
    if (!userId) return;
    setCount((prev) => {
      const newCount = prev + 1;
      writeUsageToStorage(userId, newCount);
      return newCount;
    });
  }, [userId]);

  const syncFromServer = useCallback(
    (serverCount: number) => {
      if (!userId) return;
      setCount(serverCount);
      writeUsageToStorage(userId, serverCount);
    },
    [userId]
  );

  const remaining = Math.max(0, SPACES_MAX_USAGE - count);

  return {
    count,
    maxAllowed: SPACES_MAX_USAGE,
    remaining,
    canGenerate: remaining > 0,
    incrementUsage,
    syncFromServer,
  };
}
